import { tabs } from '@/src/experiences/tabs'
import { Routes as CardBenefitsRoutes } from '@/src/experiences/card-benefits/navigation/routes'
import { Routes as HomeRoutes } from '@/src/experiences/home/navigation/routes'
import { PointsToCashForMortgageRoutes } from '@/src/experiences/points-to-cash-for-mortgage/navigation/routes'
import { Routes as MortgageRoutes } from '@/src/experiences/mortgage/navigation/routes'
import { PointsToOmniRoutes } from '@/src/experiences/points-to-omni/navigation/routes'
import { RewardsRoutes } from '@/src/experiences/rewards/navigation/routes'
import { PointsToStatementCreditRoutes } from '@/src/experiences/points-to-statement-credit/navigation/routes'
import type { ExpoRouter } from 'expo-router'
import { CardPaymentsRoutes } from '@/src/experiences/card-payments/navigation/routes'
import { MortgageEarnRoutes } from '@/src/experiences/mortgage-earn/navigation/routes'
import { NotificationsRoutes } from '@/src/experiences/notifications/navigation/routes'
import { Routes as CardApplicationRoutes } from '@/src/experiences/card-application/navigation/routes'

/** @see {@link https://linear.app/mesarewards/issue/MESA-745/fix-deeplinking-for-routes-with-disallowed-characters} */
type StringContainingDisallowedDeepLinkCharacter = `${string}${
  | '\\'
  // | '/'
  | '*'
  | '!'
  | '@'
  | '#'
  | '?'
  | '$'
  | '^'
  | '&'
  | '~'
  | '`'
  | '='
  | '+'
  | "'"
  | '"'
  | ';'
  | ':'
  | '>'
  | '<'
  | '('
  | ')'}${string}`

export interface RouteInterface {
  route: ExpoRouter.__routes['href']
  name: string
  availableForDeepLinking: boolean
  aliases?: string[]
}

export const Routes = {
  Root: { route: '/get-started', name: 'Get Started Screen', availableForDeepLinking: true },
  Onboarding: { route: '/onboarding', name: 'Phone Screen', availableForDeepLinking: true },
  OnboardingVerifyOtp: {
    route: '/onboarding/VerifyOTP',
    name: 'OTP Screen',
    availableForDeepLinking: true,
  },
  AccountCreation: {
    route: '/onboarding/AccountCreation',
    name: 'Account Creation Screen',
    availableForDeepLinking: true,
  },
  AccountCreationVerifyPhoneOtp: {
    route: '/onboarding/AccountCreationVerifyPhoneOtp',
    name: 'Account Creation Verify Phone OTP',
    availableForDeepLinking: true,
  },
  Main: {
    route: '/main/(tabs)/home',
    name: 'Main Menu Screen',
    availableForDeepLinking: true,
    aliases: ['/main'],
  },
  InAppBrowser: {
    route: '/main/mesa-in-app-browser',
    name: 'In-App Browser',
    availableForDeepLinking: false,
  },
  Support: { route: '/main/support', name: 'Support', availableForDeepLinking: true },
  PiiMenu: { route: '/main/pii-menu', name: 'PII Menu', availableForDeepLinking: true },
  ReferAFriend: {
    route: '/main/refer-a-friend',
    name: 'Refer a friend',
    availableForDeepLinking: true,
  },
  CardHome: {
    route: `/main/(tabs)/${tabs.card.name}`,
    name: tabs.card.name,
    availableForDeepLinking: true,
    aliases: [`/main/${tabs.card.name}`],
  },
  CardBenefits: {
    route: '/main/(tabs)/card/cardBenefits',
    name: 'Card Benefits',
    availableForDeepLinking: true,
    aliases: ['/main/cardBenefits', '/main/card/cardBenefits'],
  },
  VirtualCard: {
    route: '/main/(tabs)/card/card-management/virtual-card',
    name: 'Virtual Card',
    availableForDeepLinking: true,
    aliases: ['/main/card/card-management/virtual-card'],
  },
  ActivateCard: {
    route: '/main/(tabs)/card/card-management/virtual-card/activateCard',
    name: 'Activate Card',
    availableForDeepLinking: false,
  },
  CardManagement: {
    route: '/main/(tabs)/card/card-management',
    name: 'Card Management',
    availableForDeepLinking: false,
  },
  TransactionEventDetails: {
    route: '/main/(tabs)/card/card-management/transaction-event/[id]',
    name: 'Transaction Event Details',
    availableForDeepLinking: true,
    aliases: ['/main/card/card-management/transaction-event/[id]'],
  },
  CardManagementAccounts: {
    route: '/main/(tabs)/card/card-management/accounts',
    name: 'Card Management - Accounts',
    availableForDeepLinking: true,
    aliases: ['/main/card/card-management/accounts'],
  },
  CardManagementStatements: {
    route: '/main/card/card-management/statements',
    name: 'Card Management - Statements',
    availableForDeepLinking: true,
  },
  CardManagementViewStatement: {
    route: '/main/card/card-management/statements/[id]',
    name: 'Card Management - View Statement',
    availableForDeepLinking: true,
  },
  Redemption: { route: '/main/redemption', name: 'Redemption', availableForDeepLinking: false },
  ...HomeRoutes,
  ...CardPaymentsRoutes,
  ...CardBenefitsRoutes,
  ...RewardsRoutes,
  ...PointsToStatementCreditRoutes,
  ...PointsToCashForMortgageRoutes,
  ...PointsToOmniRoutes,
  ...MortgageRoutes,
  ...MortgageEarnRoutes,
  ...NotificationsRoutes,
  ...CardApplicationRoutes,
} as const satisfies Record<string, RouteInterface>

type Routes = typeof Routes

Routes satisfies {
  [key in keyof Routes]: RouteInterface &
    // if the route is available for deep linking
    (Routes[key]['availableForDeepLinking'] extends true
      ? // and the route contains a disallowed deep link character
        Routes[key]['route'] extends StringContainingDisallowedDeepLinkCharacter
        ? // then require aliases to be present
          Routes[key] extends { aliases: infer Aliases }
          ? // and for each alias to be a string
            Aliases extends string[]
            ? // and for each alias to not contain a disallowed deep link character
              Aliases[number] extends StringContainingDisallowedDeepLinkCharacter
              ? never
              : { aliases: Routes[key]['aliases'] }
            : never
          : never
        : object
      : object)
}

export type RoutesInterface = typeof Routes

export type RouteType = keyof RoutesInterface
